import dayjs from 'dayjs';
import { DeliveryStatusEnum } from './enums/delivery-status.enum';
import { ICustomer } from './customer.model';
import { BasketOrder } from './basket-order.model';
import { DeliveryStatus, LIST_DELIVERY_STATUS } from './delivery-status.model';
import { AbstractTransport } from './abstract-transport.model';
import { TransportItem } from './transport-item.model';
import { OfferServiceTypeEnum } from './enums/offer-service-type.enum';
import { DATE_FORMAT } from './constants/input.constants';
import { IEstimationPricingOptionModel } from './estimation-pricing-option.model';
import { LIST_SERVICE_TYPE } from './offer-service-type.model';
import { LIST_MARCHANDISE_TYPE } from './offer-merchandise-type.model';
import { OfferMerchandiseTypeEnum } from './enums/offer-merchandise-type.enum';

export class TransportOrder extends AbstractTransport {
  public insuranceRate?: number;
  public priceHT: number = 0;
  public transportPrice: number = 0;
  public vatRate?: number;
  public vat: number = 0;
  public deliveryStatus: DeliveryStatusEnum | DeliveryStatus;
  public totalOtherUM?: number;
  public carbonFootprint?: number;
  public lastSituation?: string;
  public lastJustification?: string;
  public deliveryState?: string;
  public gazoleTaxRate?: number;
  public gazoleTax: number = 0;
  public incidentAmount?: number;
  public incidentCount?: number;
  public incidentOriginTransportOrder?: number;
  public incidentOriginTransportIncident?: number;
  public paymentMethod?: string;
  public externalId?: string;
  public customer?: ICustomer;
  public basketOrder?: BasketOrder;
  public refundAmount?: number;
  public insurance: number = 0;

  constructor(data: any = {}) {
    super();
    Object.assign(this, data);
    this.merchandiseType = LIST_MARCHANDISE_TYPE[data.merchandiseType || OfferMerchandiseTypeEnum.CONDITIONED];
    this.serviceType = LIST_SERVICE_TYPE[data.serviceType || OfferServiceTypeEnum.LTL];
    this.pickupDate = data.pickupDate ? dayjs(data.pickupDate) : undefined;
    this.deliveryDate = data.deliveryDate ? dayjs(data.deliveryDate) : undefined;
    this.creationDate = data.creationDate ? dayjs(data.creationDate) : undefined;
    this.deliveryMaxDate = data.deliveryMaxDate ? dayjs(data.deliveryMaxDate) : undefined;
    this.deliveryStatus = data.deliveryStatus ? LIST_DELIVERY_STATUS[data.deliveryStatus] : LIST_DELIVERY_STATUS.INITIAL;
    this.items = (data.items || []).map((item: any) => new TransportItem(item));
  }

  public ttc(): number {
    return this.ht() + this.vat;
  }

  public ht(): number {
    return this.priceHT + this.gazoleTax;
  }

  public pricingOptions(): IEstimationPricingOptionModel[] {
    return [
      {
        key: '',
        amount: this.priceHT - this.transportPrice - this.insurance,
      },
    ];
  }

  public serialize(): any {
    return {
      id: this.id,
      items: (this.items || []).map(item => item.serialize()),
      itemsCondition: this.itemsCondition,
      insuranceItem: this.insuranceItem,
      basket: this.basket,
      customerId: this.customerId,
      isHazardousMaterial: this.isHazardousMaterial,
      isControlledTemperature: this.isControlledTemperature,
      merchandiseType: this.merchandiseType.code,
      serviceType: this.serviceType?.code,
      pickupAddressStreetNumber: this.pickupAddressStreetNumber,
      pickupAddressStreet: this.pickupAddressStreet,
      pickupAddressCity: this.pickupAddressCity,
      pickupAddressZipCode: this.pickupAddressZipCode,
      pickupAddressInseeCode: this.pickupAddressInseeCode,
      pickupAddressFullname: this.pickupAddressFullname,
      pickupAddressAdditionals: this.pickupAddressAdditionals,
      pickupAddressCountry: this.pickupAddressCountry,
      pickupAddressGpsCoords: this.pickupAddressGpsCoords,
      deliveryAddressStreetNumber: this.deliveryAddressStreetNumber,
      deliveryAddressStreet: this.deliveryAddressStreet,
      deliveryAddressCity: this.deliveryAddressCity,
      deliveryAddressZipCode: this.deliveryAddressZipCode,
      deliveryAddressInseeCode: this.deliveryAddressInseeCode,
      deliveryAddressFullname: this.deliveryAddressFullname,
      deliveryAddressAdditionals: this.deliveryAddressAdditionals,
      deliveryAddressCountry: this.deliveryAddressCountry,
      deliveryAddressGpsCoords: this.deliveryAddressGpsCoords,
      deliveryContactCivility: this.deliveryContactCivility,
      deliveryContactFirstName: this.deliveryContactFirstName,
      deliveryContactLastName: this.deliveryContactLastName,
      deliveryContactMail: this.deliveryContactMail,
      deliveryContactPhone: this.deliveryContactPhone,
      deliveryAccessConstraint: this.deliveryAccessConstraint,
      deliveryLoadingConstraint: this.deliveryLoadingConstraint,
      deliveryRegisteredAddressId: this.deliveryRegisteredAddressId,
      deliveryTimeslotEnd: this.deliveryTimeslotEnd,
      deliveryTimeslotStart: this.deliveryTimeslotStart,
      deliveryDate: this.deliveryDate && dayjs(this.deliveryDate).isValid() ? dayjs(this.deliveryDate).format(DATE_FORMAT) : undefined,
      deliveryMaxDate: this.deliveryMaxDate && dayjs(this.deliveryMaxDate).isValid() ? dayjs(this.deliveryMaxDate).format(DATE_FORMAT) : undefined,
      deliveryComments: this.deliveryComments,
      deliveryFloor: this.deliveryFloor,
      deliveryPhoneCall: this.deliveryPhoneCall,
      pickupContactCivility: this.pickupContactCivility,
      pickupContactFirstName: this.pickupContactFirstName,
      pickupContactLastName: this.pickupContactLastName,
      pickupContactMail: this.pickupContactMail,
      pickupContactPhone: this.pickupContactPhone,
      pickupAccessConstraint: this.pickupAccessConstraint,
      pickupLoadingConstraint: this.pickupLoadingConstraint,
      pickupRegisteredAddressId: this.pickupRegisteredAddressId,
      pickupTimeslotEnd: this.pickupTimeslotEnd,
      pickupTimeslotStart: this.pickupTimeslotStart,
      pickupMeeting: this.pickupMeeting,
      pickupFloor: this.pickupFloor,
      pickupComments: this.pickupComments,
      pickupPhoneCall: this.pickupPhoneCall,
      pickupDate: this.pickupDate && dayjs(this.pickupDate).isValid() ? dayjs(this.pickupDate).format(DATE_FORMAT) : undefined,

      totalPallets: this.totalPallets,
      totalParcel: this.totalParcel,
      totalWeight: this.totalWeight,
      transportExtraConditions: this.transportExtraConditions,
      transporter: { id: this.transporter?.id },
      creationDate: this.creationDate && dayjs(this.creationDate).isValid() ? dayjs(this.creationDate).toJSON() : undefined,
      creatorLogin: this.creatorLogin,
      offerId: this.offerId,

      quotationStatus: this.quotationStatus,
      quotationValidityDate: this.quotationValidityDate?.isValid() ? this.quotationValidityDate.toJSON() : undefined,
      quotationBasePrice: this.quotationBasePrice,
      quotationPriceHT: this.quotationPriceHT,
      quotationRejectionMessage: this.quotationRejectionMessage,

      itemsNature: this.itemsNature,
      goodsValue: this.goodsValue,
      euroPallet: this.euroPallet,
      billingQuantity: this.billingQuantity,
      totalVolume: this.totalVolume,
      totalLDM: this.totalLDM,
      distance: this.distance,
      outsideEuDeliveryCountry: this.outsideEuDeliveryCountry,
      outsideEuDeliveryCity: this.outsideEuDeliveryCity,
      outsideEuDeliveryComments: this.outsideEuDeliveryComments,
    };
  }
}
